import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { ChartData, ChartOptions } from 'chart.js';
import GenericChart from 'src/components-dummy/Chart/Chart';
import { ChartType, KPITypesEnum } from '../../types';
import { formatKpiWithSign } from '../../utils/utils';

interface GaugeMeterProps {
  syteRevenue: number;
  totalRevenue: number;
}

export const GaugeMeter = ({ syteRevenue, totalRevenue }: GaugeMeterProps): JSX.Element => {
  const safeTotal = Number.isFinite(totalRevenue) ? totalRevenue : 0;
  const safeSyteRevenue = Number.isFinite(syteRevenue) ? Math.max(syteRevenue, 0) : 0;
  const remaining = Math.max(safeTotal - safeSyteRevenue, 0);
  const percentage = safeTotal > 0 ? Math.min((safeSyteRevenue / safeTotal) * 100, 100) : 0;

  const data = useMemo<ChartData<'doughnut'>>(
    () => ({
      labels: ['Syte Revenue', 'Other Website Revenue'],
      datasets: [
        {
          data: [safeSyteRevenue, remaining],
          backgroundColor: ['#005CCC', '#E6E8ED'],
          borderWidth: 0,
          hoverOffset: 2,
        },
      ],
    }),
    [safeSyteRevenue, remaining]
  );

  const options = useMemo<ChartOptions<'doughnut'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      cutout: '70%',
      rotation: -90,
      circumference: 180,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: ctx => {
              const label = ctx.label || '';
              const value = Number(ctx.raw || 0);
              const formatted = formatKpiWithSign(KPITypesEnum.Revenue, value);
              return `${label}: ${formatted}`;
            },
          },
        },
      },
    }),
    []
  );

  return (
    <Box display='flex' flexDirection='column' alignItems='center' width='100%'>
      <Box position='relative' width={280} height={160}>
        <GenericChart type={ChartType.Doughnut} data={data} options={options} />
        <Box
          position='absolute'
          left={0}
          right={0}
          bottom={0}
          textAlign='center'
          display='flex'
          flexDirection='column'
          alignItems='center'
          gap={0.2}
        >
          <Typography variant='h6' sx={{ fontWeight: 700, color: '#1E1E1E' }}>
            {percentage.toFixed(1)}%
          </Typography>
          <Typography variant='body2' sx={{ color: '#6E6E71' }}>
            of Website Revenue
          </Typography>
        </Box>
      </Box>
      <Typography variant='subtitle2' sx={{ mt: 1, color: '#1E1E1E', fontWeight: 600 }}>
        Syte Revenue {formatKpiWithSign(KPITypesEnum.Revenue, safeSyteRevenue)}
      </Typography>
    </Box>
  );
};
